// Cita de WhatsApp (respuesta a otro mensaje) a partir del raw_payload que
// persiste el backend (Evolution / WhatsApp).
//
// Dónde vive el contextInfo (mismo criterio que quotedMessageIdOf del backend):
//  - texto simple (message.conversation): en la RAÍZ del payload;
//  - imágenes, extendedText, etc.: dentro de message.<tipo>.contextInfo.
//
// Nunca lanza: un payload malformado o sin cita devuelve null.

// Clave del mensaje de WhatsApp → tipo de media de la cita.
const QUOTED_MEDIA_KEYS = {
  imageMessage: "image",
  audioMessage: "audio",
  documentMessage: "document",
  documentWithCaptionMessage: "document",
  videoMessage: "video",
  stickerMessage: "sticker",
};

const isObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const nonEmptyString = (v) => (typeof v === "string" && v.trim() ? v : null);

function parsePayload(raw) {
  if (isObject(raw)) return raw;
  if (typeof raw !== "string" || !raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return isObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function findContextInfo(payload) {
  if (isObject(payload.contextInfo) && nonEmptyString(payload.contextInfo.stanzaId)) {
    return payload.contextInfo;
  }
  if (!isObject(payload.message)) return null;
  for (const value of Object.values(payload.message)) {
    if (isObject(value) && isObject(value.contextInfo) && nonEmptyString(value.contextInfo.stanzaId)) {
      return value.contextInfo;
    }
  }
  return null;
}

// Texto y tipo de un quotedMessage (objeto de mensaje de WhatsApp).
function describeQuotedMessage(quoted) {
  if (!isObject(quoted)) return { text: null, kind: null };
  const text =
    nonEmptyString(quoted.conversation) ||
    nonEmptyString(quoted.extendedTextMessage?.text) ||
    nonEmptyString(quoted.imageMessage?.caption) ||
    nonEmptyString(quoted.videoMessage?.caption) ||
    nonEmptyString(quoted.documentMessage?.caption) ||
    nonEmptyString(quoted.documentWithCaptionMessage?.message?.documentMessage?.caption) ||
    null;
  const mediaKey = Object.keys(QUOTED_MEDIA_KEYS).find((k) => isObject(quoted[k]));
  return { text, kind: mediaKey ? QUOTED_MEDIA_KEYS[mediaKey] : "text" };
}

/**
 * Extrae la cita de un mensaje.
 * @param {object|null|undefined} msg mensaje del hilo (con raw_payload)
 * @returns {{ stanzaId: string, participant: string|null, text: string|null, kind: string|null } | null}
 *   `kind`: "text" | "image" | "audio" | "document" | "video" | "sticker", o
 *   null si el payload no trae el quotedMessage (solo el id citado).
 */
export function extractQuote(msg) {
  try {
    const payload = parsePayload(msg?.raw_payload);
    if (!payload) return null;
    const ctx = findContextInfo(payload);
    if (!ctx) return null;
    const { text, kind } = describeQuotedMessage(ctx.quotedMessage);
    return {
      stanzaId: ctx.stanzaId,
      participant: nonEmptyString(ctx.participant),
      text,
      kind,
    };
  } catch {
    return null;
  }
}

// content_type de un mensaje del hilo → tipo de media de la cita.
const CONTENT_TYPE_KIND = {
  image: "image",
  audio: "audio",
  voice_note: "audio",
  document: "document",
  video: "video",
  sticker: "sticker",
};

/**
 * Vista previa de un mensaje citado que SÍ está en el hilo cargado.
 * @returns {{ text: string|null, kind: string }}
 */
export function previewOfThreadMessage(msg) {
  return {
    text: nonEmptyString(msg?.content),
    kind: CONTENT_TYPE_KIND[msg?.content_type] || "text",
  };
}
